import type { VersionConfig } from '@/shared/ui/SidePanelWrapper';

/**
 * Widget store bindings — runtime injection of cross-app stores into widget
 * leaf features.
 *
 * Why: per FSD layering, `features/<x>-widget/` MUST NOT value-import from
 * `@/app/store/...` or sibling features (`@/features/chats`, etc.). Most
 * cross-feature lifts use dependency injection through React props, but
 * widget leaf features have many call sites that read from `useChatStore`,
 * `useUIStore`, and `useWidgetStore` (both reactively via selector hooks
 * and non-reactively via `.getState()`) inside callbacks where
 * prop-injection is not viable across a large widget surface.
 *
 * Pattern (analogous to `features/chats/lib/chat-store-bindings.ts`):
 * an app-level bootstrap calls
 * `configureWidgetStores({ chatStore, widgetStore, uiStore })` ONCE before
 * any widget code runs. Widget internals then call `getWidgetStores()` to
 * obtain the stores via the binding, never via direct import.
 *
 * **Lives in `entities/widget/lib/`** (NOT `features/<x>-widget/lib/`)
 * because:
 *   1. The seam serves ALL widget leaf features uniformly (single source
 *      of binding).
 *   2. `entities/widget` already hosts the shared widget contract
 *      (processors, hooks, UI primitives).
 *   3. The cross-entity isolation rule is preserved: this file does NOT
 *      value-import from `@/app/`, `@/features/`, or `@/pages/` — the
 *      binding types are structural mirrors defined locally.
 *
 * The page-side wiring code (`apps/agent-studio/src/main.tsx`) is the
 * single place where the concrete `useChatStore` / `useUIStore` /
 * `useWidgetStore` references meet the structural binding contract.
 * Tests stub `@/entities/widget` directly via `vi.mock` per the
 * established widget test pattern.
 */

// --- Structural binding types --------------------------------------------
//
// Mirror the narrow surface widgets actually consume. They MUST stay
// structurally compatible with the concrete `@/app/store/...` modules and
// `@/features/chats` exports at the wiring site. If those modules diverge,
// the wiring site's structural assignment will fail at compile time —
// that is the contract enforcement.

/**
 * Bivariant call signature mirroring zustand's `UseBoundStore`:
 * - `useStore()` returns the full store
 * - `useStore(selector)` returns the selected slice
 * - `useStore.getState()` returns the full store synchronously
 * - `useStore.subscribe(listener)` subscribes to store changes (raw zustand
 *   subscribe — listener receives `(state, prevState)`)
 *
 * Concrete state shape is intentionally broad — internals supply selectors
 * with their own narrow expectations and cast at the boundary.
 */
export interface ZustandLikeStoreHook<TState> {
  (): TState;
  <U>(selector: (state: TState) => U): U;
  getState: () => TState;
  subscribe: (listener: (state: TState, prevState: TState) => void) => () => void;
}

/**
 * Subset of `@/app/store/widget` consumed by widget leaf features. The full
 * surface is wide; this binding exposes the zustand hook plus the most
 * commonly-used selector hooks so widgets can reach what they need without
 * importing from `@/app/store/widget` directly.
 *
 * Concrete shapes (`WidgetPanelState`, `WidgetActions`, `WidgetVersions`)
 * are typed broadly here — internals supply narrower selector types and
 * cast at the boundary, mirroring the chats binding pattern.
 */
export interface WidgetWidgetStoreBinding {
  /**
   * The zustand `useWidgetStore` hook. Widget internals call
   * `getWidgetStores().widgetStore.useWidgetStore.getState()` from inside
   * callbacks, and `useWidgetStore(selector)` is allowed too — it is the
   * same stable reference returned by zustand's `create()`.
   */
  useWidgetStore: ZustandLikeStoreHook<WidgetWidgetStoreState>;
  /** Compatibility selector (`useWidgetStore` slice with widget-panel fields). */
  useWidgetPanel: () => WidgetWidgetPanelView;
  /** Selector hook returning the current `widget` slice. */
  useWidget: () => WidgetWidgetSlice | null;
  /** Selector hook returning the current `error` slice. */
  useWidgetError: () => string | null;
  /** Selector hook returning `{ clearError, ...}` action methods. */
  useWidgetActions: () => WidgetWidgetActionsView;
  /**
   * Pre-bound `makeUseWidgetVersions(useWidgetStore)` hook.
   * Widget internals call `useWidgetVersions(agentId)`. Returns the
   * shared-layer `VersionConfig` so widget UIs can pass the result
   * straight to `<SidePanel headerConfig={{ versionConfig }} />`.
   */
  useWidgetVersions: (agentId: string) => VersionConfig | undefined;
  /**
   * Pre-bound `makeUseCreateArtifactVersion(createArtifact)` hook.
   * Widget internals call `useCreateArtifactVersion(...)`.
   */
  useCreateArtifactVersion: (...args: unknown[]) => unknown;
}

/**
 * Loose state shape — only the fields/methods widgets actually touch are
 * documented here. Concrete typing happens at the wiring site (which
 * structurally assigns the real `WidgetStore` to `WidgetWidgetStoreState`).
 *
 * Index signature accommodates fields widget internals reach for via
 * selectors but doesn't need to enumerate (e.g. internal store actions).
 *
 * `getActiveVersion` and
 * `storeWidgetVersion` are explicitly declared on the binding interface
 * (not buried under `[key: string]: unknown`) so consumers can read them
 * via the typed selector form `useWidgetStore((s) => s.getActiveVersion)`
 * WITHOUT an inline `as unknown as { getActiveVersion: ... }` cast. If the
 * concrete `@/app/store/widget` ever renames or removes either symbol, the
 * structural assignment at the wiring site (`apps/agent-studio/src/main.tsx`
 * via `configureWidgetStores`) will fail at compile time — that is the
 * contract enforcement, not a per-call inline cast that silently widens to
 * `unknown`.
 */
export interface WidgetWidgetStoreState {
  widget: WidgetWidgetSlice | null;
  loading: boolean;
  error: string | null;
  /** Every widget version of the conversation, per agent. Mirrors `WidgetState.agentHistories`. */
  agentHistories: Record<
    string,
    { versions: { messageId: string; widget: { key?: unknown; [key: string]: unknown } }[] }
  >;
  /**
   * Returns the currently-active widget version for the given agent, or
   * `null` if none is active. Mirrors
   * `WidgetActions.getActiveVersion` from
   * `@/app/store/widget/widget.store.types`. Loose `widget` shape (just
   * the fields the email-flow-widget consumer narrows at use:
   * `exchangeId`, `data`, `messageId`).
   */
  getActiveVersion: (agentId: string) => WidgetActiveVersion | null;
  /**
   * Persist a new widget version snapshot to the store. Mirrors
   * `WidgetActions.storeWidgetVersion` from
   * `@/app/store/widget/widget.store.types` — the concrete signature is
   * `(messageId, agentId, widgetWithMeta, title?)`, where `widgetWithMeta`
   * carries the full `WidgetWithMeta` (key, agentName, data, version,
   * exchangeId, messageId, agentId). Kept loose here (`payload:
   * StoreWidgetVersionPayload`) so the binding does not need to import
   * the concrete `WidgetWithMeta` type — narrowing happens at the
   * email-flow-widget consumer.
   */
  storeWidgetVersion: (
    messageId: string,
    agentId: string,
    payload: StoreWidgetVersionPayload,
    title?: string,
  ) => void;
  /**
   * Publish whether this editor holds manual edits the user has not saved to an artifact, so the chat
   * composer can warn before sending a prompt the agent would answer from the last SAVED version.
   * `source` must be unique per editor instance (e.g. `` `${agentId}:preview` ``) so two editors on one
   * widget cannot clear each other's flag. Mirrors `WidgetActions.setWidgetUnsavedEdits`. Declared
   * explicitly (not left to the index signature) for the same reason as the two above: the wiring site
   * then fails at compile time if the concrete store drops it.
   */
  setWidgetUnsavedEdits: (source: string, dirty: boolean) => void;
  // Other actions/state widgets may reach for via selectors.
  [key: string]: unknown;
}

/**
 * Loose mirror of the active-version envelope returned by
 * `getActiveVersion(agentId)`. Only the fields widget consumers actually
 * read at the call site are documented here; everything else stays under
 * the index signature so the binding does not pin to a concrete
 * `WidgetVersion` import (which would force `entities/widget` to depend
 * on `@/app/store/widget/widget.store.types`).
 */
interface WidgetActiveVersion {
  /** Identifier of the chat message the active version is attached to. */
  messageId?: string;
  /** Active widget envelope — `exchangeId` + `data` consumed at use. */
  widget?: { exchangeId: string; data: unknown; [key: string]: unknown };
  [key: string]: unknown;
}

/**
 * Loose payload shape accepted by `storeWidgetVersion(messageId, agentId,
 * payload, title?)`. Mirrors the public-surface fields of `WidgetWithMeta`
 * the email-flow-widget save flow constructs at the call site. Index
 * signature accommodates extra fields the concrete `WidgetWithMeta`
 * carries that the email-flow consumer does not enumerate here.
 */
interface StoreWidgetVersionPayload {
  key: string;
  agentName: string;
  data: Record<string, unknown>;
  version: number;
  exchangeId: string;
  [key: string]: unknown;
}

export interface WidgetWidgetSlice {
  agentName: string;
  sourceAgentId?: string;
  [key: string]: unknown;
}

export interface WidgetWidgetPanelView {
  widget: WidgetWidgetSlice | null;
  loading: boolean;
  agentHistories: Record<string, unknown>;
  setWidget: (widget: unknown) => void;
  clearWidget: () => void;
  setLoading: (loading: boolean) => void;
  processWidget: (...args: unknown[]) => unknown;
}

export interface WidgetWidgetActionsView {
  clearError: () => void;
  [key: string]: unknown;
}

/**
 * Subset of `@/app/store/ui` consumed by widget leaf features. Intentionally
 * narrow: widgets typically only need to open/close side panels and observe
 * agent-modal state.
 *
 * Surface needed by `email-flow-widget`:
 *   - `useSidePanel` — selector hook returning the current side-panel slice
 *     including `closeSidePanel` (used by email-flow's `useEmailActions` to
 *     dismiss the side panel after a close action).
 *   - `useSidePanelLayoutMode` — selector hook returning the current side-
 *     panel layout mode (used by email-flow's CodeView/PreviewView to switch
 *     between fullscreen and split layouts).
 */
export interface WidgetUiStoreBinding {
  useUIStore: ZustandLikeStoreHook<WidgetUiStoreState>;
  /**
   * Selector hook returning side-panel state slice. Loose typing — consumers
   * narrow to `{ closeSidePanel, isSidePanelOpen, ... }` at the call site.
   */
  useSidePanel: () => WidgetSidePanelView;
  /**
   * Selector hook returning the current `sidePanelLayoutMode` slice.
   * Loose typing — consumers narrow to `{ sidePanelLayoutMode,
   * setSidePanelLayoutMode }` at the call site.
   */
  useSidePanelLayoutMode: () => WidgetSidePanelLayoutModeView;
}

export interface WidgetUiStoreState {
  [key: string]: unknown;
}

/** Loose return shape for `useSidePanel` — narrowed at consumer. */
interface WidgetSidePanelView {
  closeSidePanel: () => void;
  openSidePanel: (type: 'agents' | 'widget' | null) => void;
  [key: string]: unknown;
}

/** Loose return shape for `useSidePanelLayoutMode` — narrowed at consumer. */
interface WidgetSidePanelLayoutModeView {
  sidePanelLayoutMode: string;
  setSidePanelLayoutMode: (mode: string) => void;
  [key: string]: unknown;
}

/**
 * Subset of `@/features/chats` consumed by widget leaf features. Widgets
 * use chats helpers like `useChatStore`, `useChatActions`, `getChatInfo`,
 * `getFileDownloadUrl`, and `ChatMarkdownRenderer`. We expose them through
 * the binding so widget production files do not need to value-import from
 * the `@/features/chats` barrel directly.
 *
 * The binding type for `useChatStore` is the bivariant zustand hook; for
 * the helper functions / hooks, we use loose `unknown`-bearing signatures
 * — internals cast at the boundary, same as the chats widget-store hook.
 *
 * Surface needed by `image-widget`:
 *   - `useMessages` — selector hook returning the message list (image-widget
 *     scans agent messages for attached files by `assetId`).
 *   - `useUpdateAgentState` — returns the `setAgentState` action used by
 *     image-widget's mask-edit flow (`isDrawingMask` flag).
 *   - `useChatStoreInput` — selector hook returning `{ chatInput, setValue,
 *     setFiles, setOptions }` — image-widget reads `chatInput.options.settings.model`
 *     to decide if mask editing is allowed.
 *   - `useFileAttachments` — composer file attachments hook (referenceable
 *     image files in the chat composer).
 *   - `ChatMarkdownRenderer` — React component used by `copy-widget` to
 *     render markdown inside the editable plain-text channel.
 *   - `SUPPORTED_IMAGE_MODELS` — readonly tuple of supported image models;
 *     image-widget filters by `features.includes('edit-with-mask')` to
 *     enable mask UI per model.
 *   - `setInputOptions` on the chat-store state — image-widget calls
 *     `useChatStore.getState().setInputOptions(...)` to write/clear the
 *     mask key on the chat input options.
 *
 * Surface needed by `email-flow-widget`:
 *   - `useChatId` — selector hook returning the current `chatId` (used by
 *     email-flow's artifact-save / code-view to associate the saved
 *     artifact with the active chat session).
 */
export interface WidgetChatStoreBinding {
  useChatStore: ZustandLikeStoreHook<WidgetChatStoreState>;
  useChatActions: () => WidgetChatActionsView;
  getChatInfo: (params: { contextId: string }) => Promise<{ title?: string }>;
  getFileDownloadUrl: (assetId: string) => Promise<string>;
  /**
   * Selector hook returning the current `chatId` (or `null`/`undefined` if no
   * chat is active). Concrete impl is `useChatStore((s) => s.chatId)` — kept
   * loose here to avoid importing chats types into entities/widget.
   */
  useChatId: () => string | null | undefined;
  /**
   * Selector hook returning the current `messages` slice. Concrete shape is
   * `Message[]` from `@/features/chats/model/chats.types` (UserMessage |
   * AgentMessage); kept as `WidgetChatMessage[]` here to avoid importing
   * the chats types into entities/widget. Widget internals narrow at use.
   */
  useMessages: () => WidgetChatMessage[];
  /**
   * Selector hook returning the chat-store `setAgentState` action. Used by
   * image-widget to set the `isDrawingMask` agent-state flag while a user
   * is editing an image mask.
   */
  useUpdateAgentState: () => (
    updater: (prev: Record<string, unknown>) => Record<string, unknown>,
  ) => void;
  /**
   * Selector hook returning a stable bag of input controls and current
   * input value. Image-widget reads `chatInput.options.settings.model`.
   */
  useChatStoreInput: () => WidgetChatInputView;
  /**
   * Composer file-attachments hook. Image-widget uses
   * `{ files, handleFileReference, handleRemoveFile }` to manage the
   * referenceable image attached to the chat composer.
   */
  useFileAttachments: (props: WidgetUseFileAttachmentsProps) => WidgetUseFileAttachmentsReturn;
  /**
   * Markdown renderer component. Copy-widget renders this inside the
   * editable text channel; social-brief-widget renders many compact inline
   * variants (`disableAnimation`, `inline`) inside its sub-sections.
   *
   * Optional `disableAnimation`, `inline`, `typewriter`, `isStreaming`
   * props match the concrete `ChatMarkdownRendererProps` surface — see
   * `@/features/chats/ui/ChatMarkdownRenderer/ChatMarkdownRenderer.types`.
   */
  ChatMarkdownRenderer: React.ComponentType<{
    content: string;
    typewriter?: boolean;
    disableAnimation?: boolean;
    isStreaming?: boolean;
    inline?: boolean;
  }>;
  /**
   * Readonly list of supported image models. Image-widget filters by
   * `features.includes('edit-with-mask')` to gate the mask UI.
   */
  SUPPORTED_IMAGE_MODELS: ReadonlyArray<{
    label: string;
    value: string;
    provider: string;
    features: ReadonlyArray<string>;
  }>;
}

export interface WidgetChatStoreState {
  /**
   * `setInputOptions` on the chat-store state. Image-widget calls
   * `useChatStore.getState().setInputOptions(...)` (non-reactive write
   * inside callbacks) to set/clear the mask key on chat input options.
   */
  setInputOptions: (
    setter:
      | Record<string, unknown>
      | undefined
      | ((prev: Record<string, unknown> | undefined) => Record<string, unknown> | undefined),
  ) => void;
  /**
   * Current chat input bag — `aiStatus` and `chatInput.options` observed
   * by image-widget's `useChatStore.subscribe(...)` to detect mask-clear
   * and send-start transitions.
   */
  chatInput: { options?: Record<string, unknown> };
  aiStatus: string | null;
  [key: string]: unknown;
}

/**
 * Loose `Message`-like shape — widget internals narrow at use. The chats
 * concrete type is `UserMessage | AgentMessage` (discriminated by
 * `type: 'user' | 'agent'`); we expose just enough fields for image-widget
 * to scan agent messages for attached files by `assetId`.
 */
export interface WidgetChatMessage {
  type: 'user' | 'agent';
  files?: ReadonlyArray<{ assetId: string; url: string; mimeType: string; [key: string]: unknown }>;
  [key: string]: unknown;
}

/** Loose return shape for `useChatStoreInput` — narrowed at consumer. */
export interface WidgetChatInputView {
  chatInput: { options?: Record<string, unknown>; [key: string]: unknown };
  setValue: (value: string) => void;
  setFiles: (files: unknown[]) => void;
  setOptions: WidgetChatStoreState['setInputOptions'];
}

/** Loose props for `useFileAttachments`. */
export interface WidgetUseFileAttachmentsProps {
  getModelFromContext?: () => string | undefined;
}

/** Loose return shape for `useFileAttachments` — narrowed at consumer. */
export interface WidgetUseFileAttachmentsReturn {
  files: ReadonlyArray<{ assetId: string; [key: string]: unknown }>;
  /**
   * Reference an existing file in the chat composer. The concrete chats
   * implementation accepts `FileAssetWithUrl` (≈ FileAsset + url); we keep
   * the binding shape loose (`unknown`-bearing) so widget consumers don't
   * need an index-signature dance on their own narrow types.
   */
  handleFileReference: (file: unknown) => void;
  handleRemoveFile: (assetId: string) => void;
  [key: string]: unknown;
}

export interface WidgetChatActionsView {
  /**
   * `agentId` + `prompt` are the universal required fields; widgets that
   * need to attach extras (e.g. strategic-territories-widget passes
   * `inputs` + `files`) may include any additional `[key: string]: unknown`
   * params — narrowed at the concrete callsite. Keeps this binding loose
   * so the widget slice doesn't need to import the chats `ChatActions`
   * type just to pass extras.
   */
  sendMessage: (params: { agentId: string; prompt: string; [key: string]: unknown }) => unknown;
  [key: string]: unknown;
}

/** Bag of stores widget leaf features consume from `@/app/...` + chats. */
export interface WidgetStoreBindings {
  chatStore: WidgetChatStoreBinding;
  widgetStore: WidgetWidgetStoreBinding;
  uiStore: WidgetUiStoreBinding;
}

// --- Module-level binding state ------------------------------------------

let bindings: WidgetStoreBindings | null = null;

/**
 * Wire concrete app stores + chats helpers into the widget leaf features.
 * Call ONCE at app bootstrap, BEFORE any widget code runs (e.g. before the
 * React tree mounts). Subsequent calls overwrite the binding — useful for
 * tests.
 */
export function configureWidgetStores(next: WidgetStoreBindings): void {
  bindings = next;
}

/**
 * Reset the binding to unconfigured state. Test-only escape hatch — call
 * from `afterEach` if a test mutated the binding via `configureWidgetStores`.
 */
export function resetWidgetStoreBindings(): void {
  bindings = null;
}

/**
 * Retrieve the bound stores. Throws a clear error if the bootstrap forgot
 * to call `configureWidgetStores` — fail-fast beats silent undefined
 * dereference inside a deeply-nested widget render or callback.
 */
export function getWidgetStores(): WidgetStoreBindings {
  if (!bindings) {
    throw new Error(
      '[entities/widget] Widget store bindings not configured. Call `configureWidgetStores({ chatStore, widgetStore, uiStore })` at app bootstrap before any widget code runs (see entities/widget/lib/widget-store-bindings.ts).',
    );
  }
  return bindings;
}

/**
 * Test helper: returns the current bindings or `null` (without throwing).
 * Useful for setup-time guards in test fixtures.
 */
export function peekWidgetStoreBindings(): WidgetStoreBindings | null {
  return bindings;
}
